<template>
  <div>
    <!-- 更新可用通知 -->
    <el-dialog
      v-model="showUpdateDialog"
      title="发现新版本"
      width="500px"
      :before-close="handleCloseUpdate"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div class="update-content">
        <div class="update-icon">
          <el-icon size="48" color="#409EFF">
            <Download />
          </el-icon>
        </div>
        <div class="update-info">
          <p><strong>新版本: {{ updateInfo.version }}</strong></p>
          <p>发布日期: {{ formatDate(updateInfo.releaseDate) }}</p>
          <div v-if="updateInfo.releaseNotes" class="release-notes">
            <p><strong>更新内容:</strong></p>
            <div v-html="updateInfo.releaseNotes"></div>
          </div>
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="laterUpdate">稍后提醒</el-button>
          <el-button type="primary" @click="startDownload">立即更新</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 下载进度对话框 -->
    <el-dialog
      v-model="showDownloadDialog"
      title="正在下载更新"
      width="450px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
    >
      <div class="download-content">
        <div class="progress-info">
          <p>正在下载新版本 {{ updateInfo.version }}</p>
          <el-progress 
            :percentage="downloadProgress" 
            :stroke-width="8"
            :text-inside="true"
          />
          <div class="download-stats">
            <span>{{ formatBytes(downloadedBytes) }} / {{ formatBytes(totalBytes) }}</span>
            <span class="download-speed">{{ formatBytes(bytesPerSecond) }}/s</span>
          </div>
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="cancelDownload">取消下载</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 下载完成对话框 -->
    <el-dialog
      v-model="showInstallDialog"
      title="更新下载完成"
      width="400px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div class="install-content">
        <div class="install-icon">
          <el-icon size="48" color="#67C23A">
            <Check />
          </el-icon>
        </div>
        <p>新版本已下载完成，是否立即重启应用安装更新？</p>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="laterInstall">稍后安装</el-button>
          <el-button type="primary" @click="installUpdate">立即重启</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 更新状态通知 -->
    <div v-if="statusMessage" class="status-notification">
      {{ statusMessage }}
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Download, Check } from '@element-plus/icons-vue'

// 响应式数据
const showUpdateDialog = ref(false)
const showDownloadDialog = ref(false)
const showInstallDialog = ref(false)
const updateInfo = ref({})
const downloadProgress = ref(0)
const downloadedBytes = ref(0)
const totalBytes = ref(0)
const bytesPerSecond = ref(0)
const statusMessage = ref('')

// 检查是否在Electron环境中
const isElectron = ref(false)

// 组件挂载时初始化
onMounted(() => {
  if (window.electron && window.electron.updater) {
    isElectron.value = true
    initializeUpdater()
  }
})

// 组件卸载时清理监听器
onUnmounted(() => {
  if (isElectron.value && window.electron.updater) {
    window.electron.updater.removeAllListeners('update-available')
    window.electron.updater.removeAllListeners('update-downloaded')
    window.electron.updater.removeAllListeners('download-progress')
    window.electron.updater.removeAllListeners('update-status')
  }
})

// 初始化更新器
function initializeUpdater() {
  // 监听更新可用事件
  window.electron.updater.onUpdateAvailable((info) => {
    console.log('检测到新版本:', info)
    updateInfo.value = info
    showUpdateDialog.value = true
  })

  // 监听更新下载完成事件
  window.electron.updater.onUpdateDownloaded((info) => {
    console.log('更新下载完成:', info)
    showDownloadDialog.value = false
    showInstallDialog.value = true
  })

  // 监听下载进度事件
  window.electron.updater.onDownloadProgress((progress) => {
    console.log('下载进度:', progress)
    downloadProgress.value = Math.round(progress.percent)
    downloadedBytes.value = progress.transferred
    totalBytes.value = progress.total
    bytesPerSecond.value = progress.bytesPerSecond
  })

  // 监听更新状态事件
  window.electron.updater.onUpdateStatus((status) => {
    console.log('更新状态:', status)
    statusMessage.value = status
    
    // 显示状态消息
    if (status.includes('错误') || status.includes('失败')) {
      ElMessage.error(status)
    } else if (status.includes('最新版本')) {
      ElMessage.success(status)
    } else {
      ElMessage.info(status)
    }
    
    // 清除状态消息
    setTimeout(() => {
      statusMessage.value = ''
    }, 5000)
  })
}

// 处理关闭更新对话框
function handleCloseUpdate() {
  showUpdateDialog.value = false
}

// 稍后更新
function laterUpdate() {
  showUpdateDialog.value = false
  // ElMessage.info('您可以稍后在设置中检查更新')
}

// 开始下载更新
function startDownload() {
  showUpdateDialog.value = false
  showDownloadDialog.value = true
  downloadProgress.value = 0
  
  // 发送下载更新请求
  window.electron.updater.downloadUpdate()
  ElMessage.info('开始下载更新...')
}

// 稍后安装
function laterInstall() {
  showInstallDialog.value = false
  ElMessage.info('更新已下载，您可以稍后重启应用安装')
}

// 安装更新
function installUpdate() {
  showInstallDialog.value = false
  ElMessage.info('正在重启应用安装更新...')
  
  // 发送安装更新请求
  window.electron.updater.installUpdate()
}

// 手动检查更新
function checkForUpdates() {
  if (isElectron.value && window.electron.updater) {
    // ElMessage.info('正在检查更新...')
    window.electron.updater.checkForUpdates()
  } else {
    ElMessage.warning('此功能仅在客户端中可用')
  }
}

// 格式化日期
function formatDate(date) {
  if (!date) return '未知'
  const d = new Date(date)
  return d.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 格式化字节大小
function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B'
  
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// 取消下载
function cancelDownload() {
  showDownloadDialog.value = false
  
  // 重置下载状态
  downloadProgress.value = 0
  downloadedBytes.value = 0
  totalBytes.value = 0
  bytesPerSecond.value = 0
  
  // 如果Electron更新器支持取消下载功能
  if (isElectron.value && window.electron.updater && window.electron.updater.cancelDownload) {
    window.electron.updater.cancelDownload()
  }
  
  ElMessage.info('下载已取消')
}

// 导出方法供外部调用
defineExpose({
  checkForUpdates
})
</script>

<style scoped>
.update-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 0;
}

.update-icon {
  flex-shrink: 0;
}

.update-info {
  flex: 1;
}

.update-info p {
  margin: 8px 0;
  color: #606266;
}

.release-notes {
  margin-top: 16px;
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
}

.download-content {
  padding: 16px 0;
}

.progress-info p {
  margin-bottom: 16px;
  text-align: center;
  color: #606266;
}

.download-stats {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}

.download-speed {
  color: #409EFF;
}

.install-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
}

.install-icon {
  margin-bottom: 16px;
}

.install-content p {
  text-align: center;
  color: #606266;
  margin: 0;
}

.status-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  z-index: 9999;
  font-size: 14px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style> 